import axios, { AxiosError, type AxiosInstance } from 'axios';

import { usePosStore } from '@/features/pos/store';

import { queryClient } from '@/app/query-client';

import { useAuthStore } from '@/shared/auth/auth-store';

import { ApiError, AuthError, ForbiddenError, NetworkError, ValidationError } from './errors';

/**
 * Single shared axios instance.
 *
 * - In dev, baseURL is empty so requests like `/api/...` go through the Vite
 *   proxy (vite.config.ts) to `http://127.0.0.1:8001/api/...`. This avoids
 *   CORS during dev (CORS is still wired on the backend for production).
 * - In production, set VITE_API_BASE_URL to the API origin.
 */
const baseURL = import.meta.env.PROD ? import.meta.env.VITE_API_BASE_URL : '';

export const http: AxiosInstance = axios.create({
  baseURL,
  withCredentials: false,
  headers: { Accept: 'application/json' },
});

// --- request: attach token + Accept-Language --------------------------------
http.interceptors.request.use((cfg) => {
  const token = useAuthStore.getState().token;
  if (token) {
    cfg.headers = cfg.headers ?? {};
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  const lang = useAuthStore.getState().locale ?? document.documentElement.lang ?? 'ar';
  cfg.headers!['Accept-Language'] = lang;
  return cfg;
});

// --- response: unwrap envelope, map errors ----------------------------------
http.interceptors.response.use(
  (res) => {
    const body = res.data;
    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success === false) {
        throw new ApiError(res.status, body.message ?? 'Request failed', body);
      }
      // unwrap data; preserve top-level meta if present (totalRows etc.)
      res.data = 'data' in body ? body.data : body;
    }
    // A committed financial write changes every date/warehouse view of the
    // dashboard. Invalidate here to also cover imports and generic CRUD pages.
    const method = res.config.method?.toLowerCase();
    const path = res.config.url?.split('?')[0] ?? '';
    if (
      method &&
      ['post', 'put', 'patch', 'delete'].includes(method) &&
      /^\/api\/(?:pos\/create_pos|sales|purchases|expenses|returns|payment_sale|payment_purchase|payment_sales_returns|payment_purchases_returns|sales_delete_by_selection|purchases_delete_by_selection|store_import_sales|store_import_purchases|accounting)(?:\/|$)/.test(path)
    ) {
      void queryClient.invalidateQueries({ queryKey: ['restaurant'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['accounting', 'v2', 'dashboard'] });
    }
    return res;
  },
  (err: AxiosError<{ message?: string; errors?: Record<string, string[]> }>) => {
    if (!err.response) {
      throw new NetworkError(err.message || 'Network error');
    }
    const { status, data } = err.response;
    const message = data?.message ?? err.message;

    if (status === 401) {
      useAuthStore.getState().logout();
      throw new AuthError(message);
    }
    if (status === 403) throw new ForbiddenError(message);
    if (status === 422) throw new ValidationError(message, data?.errors ?? {});
    throw new ApiError(status, message, data);
  },
);

// Cached business data must never survive an account change on a shared till.
useAuthStore.subscribe((state, previous) => {
  if (state.token !== previous.token) {
    void queryClient.cancelQueries();
    queryClient.clear();
    usePosStore.getState().clear();
    usePosStore.setState({ warehouseId: null, customerId: null, customerName: null });
  }
});
